import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Mail, Phone, Download, Users, ArrowUp, ArrowDown, Send, X, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import api from '../api/client';
import FormDialog from '../components/FormDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import DetailSheet from '../components/DetailSheet';
import RowActions from '../components/RowActions';
import EmptyState from '../components/EmptyState';
import { SkeletonRow } from '../components/Skeleton';
import BulkActionsBar from '../components/BulkActionsBar';
import { useLastSeen, useAutoMarkSeen } from '../hooks/useLastSeen';
import { useBulkSelectionKeyboard } from '../hooks/useBulkSelectionKeyboard';
import InlineStatus from '../components/InlineStatus';
import { exportToCsv } from '../utils/export';
import { sortNewestFirst } from '../utils/sort';
import { toast } from '../hooks/use-toast';

const PAGE_SIZE = 25;

const STATUS_STYLES = {
  'Actif': 'bg-[#E6FBF7] text-[#0F766E]',
  'Terminé': 'bg-[#F3F4F8] text-[#4A4B63]',
  'En pause': 'bg-[#FEF3E7] text-[#B45309]',
};
const TAG_STYLES = {
  'Premium': 'bg-[#FDF4FF] text-[#A21CAF]',
  'Entreprise': 'bg-[#EEF2FF] text-[#4F46E5]',
  'Particulier': 'bg-[#E0F2FE] text-[#0369A1]',
};

const STATIC_FIELDS = [
  { name: 'firstName', label: 'Prénom', required: true },
  { name: 'lastName', label: 'Nom', required: true },
  { name: 'email', label: 'Email', type: 'email', required: true, full: true },
  { name: 'phone', label: 'Téléphone' },
  { name: 'tag', label: 'Catégorie', type: 'select', options: ['Entreprise', 'Particulier', 'Premium'], required: true },
  // `status` est un état d'activité, pas un statut professionnel : les deux
  // cohabitent, et c'est `statut_pro` qui dit **qui finance** la formation.
  { name: 'status', label: 'Suivi', type: 'select', options: ['Actif', 'En pause', 'Terminé'] },
  { name: 'progress', label: 'Progression (%)', type: 'number' },
];

// Address fields shown when tag === 'Particulier' (or Premium without a company)
const ADDRESS_FIELDS = [
  { name: 'address_line1', label: 'Adresse', full: true, placeholder: '12 rue de la Paix', showIf: (v) => v.tag === 'Particulier' || (v.tag === 'Premium' && !v.company_id) },
  { name: 'address_line2', label: 'Complément d\'adresse', full: true, placeholder: 'Bât. B, 3ᵉ étage', showIf: (v) => v.tag === 'Particulier' || (v.tag === 'Premium' && !v.company_id) },
  { name: 'address_postcode', label: 'Code postal', placeholder: '75002', showIf: (v) => v.tag === 'Particulier' || (v.tag === 'Premium' && !v.company_id) },
  { name: 'address_city', label: 'Ville', placeholder: 'Paris', showIf: (v) => v.tag === 'Particulier' || (v.tag === 'Premium' && !v.company_id) },
  { name: 'address_country', label: 'Pays', placeholder: 'France', showIf: (v) => v.tag === 'Particulier' || (v.tag === 'Premium' && !v.company_id) },
];

export default function Apprenants() {
  useAutoMarkSeen('learners', 3000);
  const navigate = useNavigate();
  const { isNew } = useLastSeen();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('Tous');
  const [apprenants, setStagiaires] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [create, setCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  // Le référentiel des statuts professionnels et, par apprenant, le financeur
  // qui s'en déduit. **Le financeur ne se saisit jamais** : il vient du statut
  // et, pour un salarié, de l'OPCO de son entreprise — un financeur tapé à la
  // main est un financeur qu'on oublie de corriger quand l'entreprise change
  // d'OPCO, et ça ne se voit qu'au refus de prise en charge.
  const [statuts, setStatuts] = useState([]);
  const [financeurs, setFinanceurs] = useState({});

  // Reset to page 1 when filters/search change
  useEffect(() => { setPage(1); }, [search, filter, sort]);

  useEffect(() => {
    api.get('/learners').then(({ data }) => setStagiaires(sortNewestFirst(data))).catch(() => {}).finally(() => setLoading(false));
    api.get('/companies').then(({ data }) => setCompanies(data)).catch(() => {});
    api.get('/statuts-apprenant').then(({ data }) => setStatuts(data.statuts || []))
      .catch(() => setStatuts([]));
    api.get('/learners/financeurs').then(({ data }) => setFinanceurs(data.financeurs || {}))
      .catch(() => setFinanceurs({}));
  }, []);

  // Build dynamic FIELDS: company select is populated from /api/companies.
  // - Entreprise (or Premium with a company): dropdown of companies
  // - Particulier: address fields
  const FIELDS = React.useMemo(() => {
    const companyField = {
      name: 'company_id',
      label: 'Entreprise',
      type: 'select',
      required: true,
      placeholder: companies.length ? 'Sélectionner une entreprise…' : "Créez d'abord une entreprise",
      options: companies.map((c) => ({ value: c.id, label: c.name })),
      full: true,
      // Visible when tag is 'Entreprise' or 'Premium' (Premium can also use a company)
      showIf: (v) => v.tag === 'Entreprise' || v.tag === 'Premium',
    };
    // Insert company dropdown AFTER the `tag` field so the dropdown appears only once
    // the user has chosen the category — feels natural.
    // Le statut professionnel vient du référentiel, jamais écrit en dur : en
    // ajouter un ne doit demander aucune modification d'écran.
    const statutField = {
      name: 'statut_pro',
      label: 'Statut professionnel',
      type: 'select',
      full: true,
      placeholder: 'À confirmer',
      options: statuts.map((st) => ({ value: st.code, label: st.libelle })),
      help: "C'est lui qui dit qui finance : l'OPCO de l'entreprise pour un"
        + ' salarié, l’AGEFICE, le FAFCEA, le VIVEA ou le FIF PL pour un'
        + ' indépendant, France Travail pour un demandeur d’emploi.',
    };
    const next = [...STATIC_FIELDS];
    const tagIdx = next.findIndex((f) => f.name === 'tag');
    next.splice(tagIdx + 1, 0, statutField, companyField, ...ADDRESS_FIELDS);
    return next;
  }, [companies, statuts]);

  // Resolve company_id → company name so we keep the legacy `company` string in sync
  const withCompanyName = (values) => {
    const out = { ...values };
    if (values.tag === 'Particulier') {
      // Clear company if user switched to Particulier
      out.company_id = '';
      out.company = '';
    } else {
      const c = companies.find((c) => c.id === values.company_id);
      out.company = c ? c.name : (values.company || '');
    }
    return out;
  };

  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = (ids) => setSelected((s) => {
    const allSelected = ids.length > 0 && ids.every((id) => s.has(id));
    const n = new Set(s);
    if (allSelected) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    try {
      await api.post('/bulk/delete', { collection: 'learners', ids });
      setStagiaires((prev) => prev.filter((s) => !selected.has(s.id)));
      setSelected(new Set());
      toast({ title: 'Supprimés', description: `${ids.length} apprenant(s) supprimé(s).` });
    } catch {
      toast({ title: 'Erreur', description: 'Suppression groupée impossible.' });
      throw new Error();
    }
  };

  const bulkChangeStatus = async (status) => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    try {
      await api.post('/bulk/status', { collection: 'learners', ids, status });
      setStagiaires((prev) => prev.map((s) => selected.has(s.id) ? { ...s, status } : s));
      setSelected(new Set());
      toast({ title: 'Statut mis à jour', description: `${ids.length} apprenant(s) → ${status}` });
    } catch {
      toast({ title: 'Erreur' });
    }
  };

  const bulkExport = () => {
    const rows = apprenants.filter((s) => selected.has(s.id));
    exportToCsv('apprenants-selection', rows, ['id','firstName','lastName','email','company','phone','status','tag','progress','sessionsCount']);
    toast({ title: 'Export CSV', description: `${rows.length} lignes exportées.` });
  };

  const handleCreate = async (values) => {
    const resolved = withCompanyName(values);
    const payload = { ...resolved, sessionsCount: 0, progress: Number(resolved.progress) || 0, status: resolved.status || 'Actif' };
    const { data } = await api.post('/learners', payload);
    setStagiaires((prev) => [...prev, data]);
    toast({ title: 'Apprenant ajouté', description: `${data.firstName} ${data.lastName}` });
  };

  const handleUpdate = async (values) => {
    const resolved = withCompanyName(values);
    const { data } = await api.put(`/learners/${editing.id}`, { ...resolved, progress: Number(resolved.progress) || 0 });
    setStagiaires((prev) => prev.map((s) => s.id === data.id ? data : s));
    toast({ title: 'Apprenant modifié', description: `${data.firstName} ${data.lastName}` });
  };

  const handleDelete = async () => {
    await api.delete(`/learners/${deleting.id}`);
    setStagiaires((prev) => prev.filter((s) => s.id !== deleting.id));
    toast({ title: 'Apprenant supprimé' });
  };

  const filtered = apprenants.filter((s) => {
    const q = search.toLowerCase();
    const matches = `${s.firstName} ${s.lastName} ${s.email} ${s.company}`.toLowerCase().includes(q);
    const matchesFilter = filter === 'Tous' || s.status === filter;
    return matches && matchesFilter;
  }).sort((a, b) => {
    const va = a[sort.key] ?? ''; const vb = b[sort.key] ?? '';
    if (typeof va === 'number') return sort.dir === 'asc' ? va - vb : vb - va;
    return sort.dir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const paged = filtered.slice(pageStart, pageStart + PAGE_SIZE);
  const hasActiveFilter = search.trim() !== '' || filter !== 'Tous';

  // Status counts (computed once over the unfiltered list for stable pill labels)
  const counts = apprenants.reduce((acc, s) => {
    acc.Tous += 1;
    if (s.status) acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, { Tous: 0, 'Actif': 0, 'En pause': 0, 'Terminé': 0 });

  // ⌘A / Suppr / Échap keyboard shortcuts for bulk selection (selects the whole filtered list)
  useBulkSelectionKeyboard({
    items: filtered,
    selected,
    setSelected,
    onDelete: () => setBulkDeleteOpen(true),
  });

  const SortTh = ({ label, k, align = 'left' }) => {
    const active = sort.key === k;
    const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : null;
    return (
      <th
        data-testid={`sort-${k}`}
        className={`py-3.5 px-5 cursor-pointer select-none group transition-colors hover:text-[#1A1B2E] ${align === 'right' ? 'text-right' : ''}`}
        onClick={() => toggleSort(k)}
      >
        <span className={`inline-flex items-center gap-1 ${active ? 'text-[#4F46E5]' : ''}`}>
          {label}
          {Icon ? (
            <Icon className="w-3 h-3" />
          ) : (
            <ArrowUp className="w-3 h-3 text-transparent group-hover:text-[#C4C5D4] transition-colors" />
          )}
        </span>
      </th>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email, entreprise…"
            className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-[#EFF0F6] text-[13.5px] focus:border-[#C7D2FE] outline-none"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['Tous', 'Actif', 'En pause', 'Terminé'].map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={`h-10 px-3.5 rounded-xl text-[12.5px] font-semibold transition-colors inline-flex items-center gap-1.5 ${
                filter === f ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'bg-white border border-[#EFF0F6] text-[#6B6C85] hover:border-[#C7D2FE]'
              }`}
            >
              {f}
              <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${filter === f ? 'bg-white text-[#4F46E5]' : 'bg-[#F3F4F8] text-[#8E8FA5]'}`}>
                {counts[f] ?? 0}
              </span>
            </button>
          ))}
        </div>
        <div className="flex gap-2 md:ml-auto">
          <button
            onClick={() => exportToCsv('apprenants', filtered, ['id','firstName','lastName','email','company','phone','status','tag','progress','sessionsCount'])}
            className="h-10 px-3.5 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-medium text-[#4A4B63] flex items-center gap-2 hover:border-[#C7D2FE]"
          >
            <Download className="w-4 h-4" /> Exporter
          </button>
          <button onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95">
            <Plus className="w-4 h-4" /> Nouveau apprenant
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total apprenants', value: apprenants.length, color: '#4F46E5' },
          { label: 'Actifs', value: apprenants.filter((s) => s.status === 'Actif').length, color: '#14B8A6' },
          { label: 'Entreprises distinctes', value: new Set(apprenants.map((s) => s.company)).size, color: '#F59E0B' },
          { label: 'Progression moyenne', value: apprenants.length ? `${Math.round(apprenants.reduce((a, s) => a + (s.progress || 0), 0) / apprenants.length)}%` : '0%', color: '#3B82F6' },
        ].map((s) => (
          <div key={s.label} className="soft-card p-4">
            <div className="text-[12px] text-[#8E8FA5] font-medium">{s.label}</div>
            <div className="text-[22px] font-bold mt-0.5" style={{ color: s.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {typeof s.value === 'number' ? s.value.toLocaleString('fr-FR') : s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Results indicator + clear-filters */}
      <div className="flex items-center justify-between gap-3 -mb-2">
        <div className="text-[12.5px] text-[#6B6C85]" data-testid="results-count">
          {loading ? 'Chargement…' : (
            <>
              <span className="font-semibold text-[#1A1B2E]">{filtered.length}</span>{' '}
              {filtered.length === apprenants.length
                ? `apprenant${filtered.length > 1 ? 's' : ''}`
                : `résultat${filtered.length > 1 ? 's' : ''} sur ${apprenants.length}`}
            </>
          )}
        </div>
        {hasActiveFilter && !loading && (
          <button
            data-testid="clear-filters"
            onClick={() => { setSearch(''); setFilter('Tous'); }}
            className="text-[12px] font-semibold text-[#4F46E5] hover:text-[#5B3FE0] inline-flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" /> Effacer les filtres
          </button>
        )}
      </div>

      <div className="soft-card rounded-2xl [overflow:clip]">
        <table className="w-full">
          <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_#EFF0F6]">
            <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5]">
              <th className="py-3.5 pl-5 pr-1 w-10">
                <input
                  data-testid="select-all-checkbox"
                  type="checkbox"
                  checked={paged.length > 0 && paged.every((s) => selected.has(s.id))}
                  onChange={() => toggleSelectAll(paged.map((s) => s.id))}
                  className="w-4 h-4 rounded accent-[#4F46E5] cursor-pointer"
                />
              </th>
              <SortTh label="Apprenant" k="lastName" />
              <SortTh label="Entreprise" k="company" />
              <th className="py-3.5 px-5">Contact</th>
              <SortTh label="Sessions" k="sessionsCount" />
              <SortTh label="Progression" k="progress" />
              <SortTh label="Statut" k="status" />
              <th className="py-3.5 px-5"></th>
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={8} />)}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <EmptyState
                    icon={Users}
                    title={hasActiveFilter ? (search ? `Aucun résultat pour « ${search} »` : 'Aucun apprenant dans ce filtre') : 'Aucun apprenant'}
                    description={hasActiveFilter ? 'Essayez un autre terme ou effacez les filtres.' : "Créez un nouveau apprenant pour démarrer."}
                    action={hasActiveFilter ? (
                      <button onClick={() => { setSearch(''); setFilter('Tous'); }} className="h-10 px-4 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE]">Effacer les filtres</button>
                    ) : (
                      <button onClick={() => setCreate(true)} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold">Nouveau apprenant</button>
                    )}
                  />
                </td>
              </tr>
            )}
            {!loading && paged.map((s) => {
              const itemIsNew = isNew('learners', s.created_at);
              return (
              <tr key={s.id} onClick={() => setDetail(s)} className={`data-row border-b border-[#F3F4F8] last:border-0 cursor-pointer transition-colors ${selected.has(s.id) ? 'bg-[#EEF2FF]' : itemIsNew ? 'bg-[#FAF6FF] hover:bg-[#EEF2FF]' : 'hover:bg-[#FAFAFE]'}`}>
                <td className="py-4 pl-5 pr-1 w-10" onClick={(e) => e.stopPropagation()}>
                  <input
                    data-testid={`select-${s.id}`}
                    type="checkbox"
                    checked={selected.has(s.id)}
                    onChange={() => toggleSelect(s.id)}
                    className="w-4 h-4 rounded accent-[#4F46E5] cursor-pointer"
                  />
                </td>
                <td className="py-4 px-5">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full brand-gradient text-white font-semibold flex items-center justify-center text-[13px]">
                      {s.firstName?.[0]}{s.lastName?.[0]}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[13.5px] font-semibold text-[#1A1B2E] truncate">{s.firstName} {s.lastName}</span>
                        {itemIsNew && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#4F46E5] text-white text-[9.5px] font-bold tracking-wide">NOUVEAU</span>}
                      </div>
                      <span className={`inline-block mt-0.5 text-[10.5px] font-semibold px-1.5 py-0.5 rounded ${TAG_STYLES[s.tag]}`}>{s.tag}</span>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-5">
                  <div className="text-[13px] text-[#1A1B2E] truncate max-w-[220px]">{s.company || <span className="italic text-[#8E8FA5]">Non rattaché</span>}</div>
                </td>
                <td className="py-4 px-5" onClick={(e) => e.stopPropagation()}>
                  {s.email && (
                    <a href={`mailto:${s.email}`} className="text-[12.5px] text-[#4A4B63] flex items-center gap-1.5 hover:text-[#4F46E5] transition-colors truncate max-w-[220px]">
                      <Mail className="w-3 h-3 shrink-0" /> {s.email}
                    </a>
                  )}
                  {s.phone && (
                    <a href={`tel:${s.phone}`} className="text-[12px] text-[#8E8FA5] flex items-center gap-1.5 hover:text-[#4F46E5] transition-colors">
                      <Phone className="w-3 h-3 shrink-0" /> {s.phone}
                    </a>
                  )}
                </td>
                <td className="py-4 px-5 text-[13px] font-semibold text-[#1A1B2E]">{s.sessionsCount}</td>
                <td className="py-4 px-5">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 max-w-[120px] h-1.5 bg-[#F3F4F8] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${s.progress}%`, background: s.progress === 100 ? '#14B8A6' : '#4F46E5' }} />
                    </div>
                    <span className="text-[12px] font-semibold text-[#4A4B63] tabular-nums w-8 text-right">{s.progress}%</span>
                  </div>
                </td>
                <td className="py-4 px-5">
                  <InlineStatus
                    testId={`status-learner-${s.id}`}
                    value={s.status}
                    options={['Actif', 'En pause', 'Terminé']}
                    styles={STATUS_STYLES}
                    onChange={async (newStatus) => {
                      const prev = s.status;
                      setStagiaires((list) => list.map((x) => x.id === s.id ? { ...x, status: newStatus } : x));
                      try {
                        await api.put(`/learners/${s.id}`, { ...s, status: newStatus });
                        toast({ title: 'Statut modifié', description: `${s.firstName} ${s.lastName} → ${newStatus}` });
                      } catch {
                        setStagiaires((list) => list.map((x) => x.id === s.id ? { ...x, status: prev } : x));
                        toast({ title: 'Erreur' });
                        throw new Error();
                      }
                    }}
                  />
                </td>
                <td className="py-4 px-5">
                  <RowActions
                    onView={() => setDetail(s)}
                    onEdit={() => setEditing(s)}
                    onDelete={() => setDeleting(s)}
                  />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[#EFF0F6] bg-[#FAFAFE]">
            <div className="text-[12px] text-[#6B6C85]" data-testid="pagination-info">
              Affichage <span className="font-semibold text-[#1A1B2E]">{pageStart + 1}</span>–<span className="font-semibold text-[#1A1B2E]">{Math.min(pageStart + PAGE_SIZE, filtered.length)}</span> sur <span className="font-semibold text-[#1A1B2E]">{filtered.length}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                data-testid="page-prev"
                disabled={safePage === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="h-8 px-2.5 rounded-lg bg-white border border-[#EFF0F6] text-[12px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Préc.
              </button>
              <span className="text-[12px] font-semibold text-[#4A4B63] px-2 tabular-nums">
                {safePage} / {totalPages}
              </span>
              <button
                data-testid="page-next"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="h-8 px-2.5 rounded-lg bg-white border border-[#EFF0F6] text-[12px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Suiv. <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <FormDialog open={create} onOpenChange={setCreate} title="Nouveau apprenant" fields={FIELDS} onSubmit={handleCreate} />
      <FormDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title="Modifier le apprenant"
        fields={FIELDS}
        initialValues={editing ? {
          ...editing,
          // Resolve company_id from legacy `company` string if missing
          company_id: editing.company_id || companies.find((c) => c.name === editing.company)?.id || '',
        } : null}
        onSubmit={handleUpdate}
      />
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Supprimer ce apprenant ?"
        description={deleting ? `${deleting.firstName} ${deleting.lastName} sera définitivement supprimé.` : ''}
        onConfirm={handleDelete}
      />
      <DetailSheet
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        title={detail ? `${detail.firstName} ${detail.lastName}` : ''}
        subtitle={detail?.id}
        badge={detail && <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded ${STATUS_STYLES[detail.status]}`}>{detail.status}</span>}
        rows={detail ? [
          { label: 'Email', value: detail.email },
          { label: 'Téléphone', value: detail.phone },
          { label: 'Entreprise', value: detail.company },
          { label: 'Catégorie', value: detail.tag },
          // Le statut professionnel, et le financeur qui s'en déduit. Ce qui
          // manque est nommé là où il se corrige — « OPCO à renseigner sur la
          // fiche de X » et « statut à confirmer » sont deux trous différents,
          // et ils n'appellent pas le même geste.
          {
            label: 'Statut professionnel',
            value: financeurs[detail.id]?.statut_libelle
              || <span className="italic text-[#8E8FA5]">à confirmer</span>,
          },
          {
            label: 'Financeur',
            value: financeurs[detail.id]?.nom
              ? (
                <span>
                  {financeurs[detail.id].nom}
                  {financeurs[detail.id].origine === 'entreprise' && (
                    <span className="text-[11px] text-[#8E8FA5]"> · l’OPCO de son entreprise</span>
                  )}
                </span>
              )
              : <span className="text-[#B45309]">{financeurs[detail.id]?.manque || 'à confirmer'}</span>,
          },
          { label: 'Code portail', value: detail.portal_access_code || <span className="italic text-[#8E8FA5]">non généré</span> },
          { label: 'Sessions suivies', value: detail.sessionsCount },
          { label: 'Progression', value: `${detail.progress}%` },
        ] : []}
      >
        {detail && (
          <div className="mt-4 pt-4 border-t border-[#EFF0F6] space-y-2">
            <button
              data-testid={`create-quote-${detail.id}`}
              onClick={async () => {
                // Resolve company name → company_id, then navigate to /app/devis with prefill
                let companyId = '';
                if (detail.company) {
                  try {
                    const { data } = await api.get('/companies');
                    const match = data.find((c) => (c.name || '').toLowerCase() === detail.company.toLowerCase());
                    companyId = match?.id || '';
                  } catch (e) { console.warn("[silent]", e); }
                }
                const prefill = { company_id: companyId, learner_ids: [detail.id] };
                setDetail(null);
                navigate('/app/devis', { state: { prefillCreate: prefill } });
              }}
              className="w-full h-10 rounded-xl bg-white border border-[#C7D2FE] text-[#4F46E5] text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:bg-[#EEF2FF]"
            >
              <FileText className="w-4 h-4" /> Créer un devis pour ce apprenant
            </button>
            <button
              data-testid={`welcome-${detail.id}`}
              onClick={async () => {
                try {
                  const { data } = await api.post(`/learners/${detail.id}/send-welcome`);
                  toast({ title: "Email envoyé", description: `${data.sent_to} · Code ${data.access_code}` });
                  setStagiaires((prev) => prev.map((s) => s.id === detail.id ? { ...s, portal_access_code: data.access_code } : s));
                  setDetail((d) => d ? { ...d, portal_access_code: data.access_code } : d);
                } catch (e) {
                  toast({ title: "Erreur", description: e?.response?.data?.detail || "Envoi impossible" });
                }
              }}
              className="w-full h-10 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center justify-center gap-1.5"
            >
              <Send className="w-4 h-4" /> Envoyer l'email de bienvenue + code portail
            </button>
          </div>
        )}
      </DetailSheet>
      <BulkActionsBar
        count={selected.size}
        onClear={() => setSelected(new Set())}
        onDelete={() => setBulkDeleteOpen(true)}
        onExport={bulkExport}
        statusOptions={['Actif', 'En pause', 'Terminé']}
        onChangeStatus={bulkChangeStatus}
      />
      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`Supprimer ${selected.size} apprenant${selected.size > 1 ? 's' : ''} ?`}
        description={`Cette action est irréversible. ${selected.size} fiche${selected.size > 1 ? 's' : ''} sera${selected.size > 1 ? 'ont' : ''} définitivement supprimée${selected.size > 1 ? 's' : ''}.`}
        confirmLabel={`Supprimer ${selected.size} apprenant${selected.size > 1 ? 's' : ''}`}
        onConfirm={bulkDelete}
      />
    </div>
  );
}
